import { useState, useEffect, useCallback } from 'react'

/**
 * Hook for managing Stripe integration and payment processing
 * Handles Stripe pricing table setup, payment success/cancel handling
 */
const useStripeIntegration = () => {
  const [message, setMessage] = useState('')
  const [success, setSuccess] = useState(false)
  const [sessionId, setSessionId] = useState('')

  // Stripe configuration from environment variables
  const stripeConfig = {
    publishKey: process.env.REACT_APP_STRIPE_PUBLISH_KEY,
    pricingTableId: process.env.REACT_APP_PRICING_TABLE_ID,
    frontendUrl: process.env.REACT_APP_FRONTEND_URL
  }

  /**
   * Loads Stripe pricing table script and returns cleanup function
   * @returns {Function} Cleanup function to remove script
   */
  const loadStripePricingScript = useCallback(() => {
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/pricing-table.js'
    script.async = true
    document.body.appendChild(script)

    // Return cleanup function
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  /**
   * Creates Stripe pricing table element with configuration
   * @param {string} userId - User ID for client reference
   * @returns {Object} React element configuration for stripe-pricing-table
   */
  const createPricingTableElement = useCallback(
    userId => {
      return {
        'pricing-table-id': stripeConfig.pricingTableId,
        'publishable-key': stripeConfig.publishKey,
        'client-reference-id': userId
      }
    },
    [stripeConfig.pricingTableId, stripeConfig.publishKey]
  )

  /**
   * Handles URL query parameters for payment success/cancel
   */
  const handlePaymentRedirect = useCallback(() => {
    const query = new URLSearchParams(window.location.search)

    if (query.get('success')) {
      setSuccess(true)
      setSessionId(query.get('session_id'))
      setMessage('')
    }

    if (query.get('canceled')) {
      setSuccess(false)
      setSessionId('')
      setMessage(
        "Order canceled -- continue to shop around and checkout when you're ready."
      )
    }
  }, [])

  /**
   * Initialize payment redirect handling on mount
   */
  useEffect(() => {
    handlePaymentRedirect()
  }, [handlePaymentRedirect])

  /**
   * Clear payment messages
   */
  const clearMessage = useCallback(() => {
    setMessage('')
  }, [])

  /**
   * Reset payment state
   */
  const resetPaymentState = useCallback(() => {
    setMessage('')
    setSuccess(false)
    setSessionId('')
  }, [])

  /**
   * Check if payment was successful
   * @returns {boolean} True if payment was successful
   */
  const isPaymentSuccessful = useCallback(() => {
    return success && sessionId
  }, [success, sessionId])

  /**
   * Check if payment was canceled
   * @returns {boolean} True if payment was canceled
   */
  const isPaymentCanceled = useCallback(() => {
    return !success && message.includes('canceled')
  }, [success, message])

  return {
    // State
    message,
    success,
    sessionId,

    // Configuration
    stripeConfig,

    // Computed values
    isPaymentSuccessful: isPaymentSuccessful(),
    isPaymentCanceled: isPaymentCanceled(),

    // Actions
    loadStripePricingScript,
    createPricingTableElement,
    handlePaymentRedirect,
    clearMessage,
    resetPaymentState,

    // Setters
    setMessage,
    setSuccess,
    setSessionId
  }
}

export default useStripeIntegration
